/* Atlas v3 — HeroCandles: a compact candlestick chart for the leaders/laggards
   hero cards on the desk screens (Global Desk + Leverage Desk). Replaces the
   old synthesized sparkline that read as thin/empty at hero size.

   Real OHLCV bars from /api/chart-bars over the CALLER'S timeframe, drawn as
   hand-rolled SVG wicks + bodies — same idiom as the TickerSheet CandleChart,
   but stripped of volume / price-axis / crosshair / controls (it's a small,
   non-interactive hero card, so no chart-lib instance per card). Candles only;
   the full line/candle toggle lives in the TickerSheet on tap.

   `range` MUST match the timeframe the calling band prints in its label. It
   was hardcoded to 1M until 2026-08-22 while every call site rendered it under
   a "Top gainer · {tf}" heading — so a 1Y band drew a one-month chart, colored
   by the wrong period's direction (audit finding F1, the PR #855 bug class).

   Lazy-loaded when it scrolls into view + cached per ticker+range across
   re-renders, so re-sorting a band (or returning to a timeframe) doesn't
   refetch. */
import { useState, useRef, useEffect } from 'react'

const BARS_CACHE = new Map() // `${ticker}::${range}` → [{o,h,l,c,t}]

// Longer ranges return far more bars than a 150px-wide hero can draw (1D is
// ~190 five-minute bars, 1Y ~252 daily), which renders as sub-pixel mush.
// Aggregate to at most one candle per ~3px, OHLC-correctly: open of the first
// bar, close of the last, extremes across the bucket. Short series (1M ≈ 25
// bars) pass through untouched.
function bucket(data, maxN) {
  if (data.length <= maxN) return data
  const size = Math.ceil(data.length / maxN)
  const out = []
  for (let i = 0; i < data.length; i += size) {
    const g = data.slice(i, i + size)
    out.push({
      o: g[0].o,
      c: g[g.length - 1].c,
      h: Math.max(...g.map((d) => d.h)),
      l: Math.min(...g.map((d) => d.l)),
    })
  }
  return out
}

// `line` renders a directional area-line of closes instead of candlesticks
// (same fetch/cache/lazy path) — used by the Leverage + Global desk heroes.
export function HeroCandles({ ticker, range = '1M', w = 150, h = 34, line = false }) {
  const ref = useRef(null)
  const cacheKey = `${ticker}::${range}`
  const [bars, setBars] = useState(() => BARS_CACHE.get(cacheKey) || null)

  // The ticker prop changes without a remount when a desk band re-sorts, and
  // `range` changes when the desk's timeframe control moves — resync from the
  // cache (or refetch) instead of keeping the old series.
  useEffect(() => { setBars(BARS_CACHE.get(cacheKey) || null) }, [cacheKey])

  useEffect(() => {
    if (bars || !ticker) return
    const el = ref.current
    if (!el) return
    let done = false
    const load = () => {
      if (done) return
      done = true
      fetch(`/api/chart-bars?ticker=${encodeURIComponent(ticker)}&range=${encodeURIComponent(range)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          const bs = Array.isArray(d?.bars)
            ? d.bars.filter((b) => b && b.c != null)
            : []
          BARS_CACHE.set(cacheKey, bs)
          setBars(bs)
        })
        // Transient failure: show the dashed baseline but do NOT cache [] —
        // a cached failure used to stick for the whole session.
        .catch(() => { setBars([]) })
    }
    const io = new IntersectionObserver(
      (es) => { if (es.some((e) => e.isIntersecting)) { load(); io.disconnect() } },
      { rootMargin: '120px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [cacheKey, ticker, range, bars])

  // Fallback dashed baseline while loading / when no data.
  if (!bars || bars.length < 2) {
    return (
      <svg ref={ref} width={w} height={h} style={{ display: 'block' }}>
        <line x1="2" y1={h / 2} x2={w - 2} y2={h / 2}
          stroke="hsl(var(--studio-border))" strokeWidth="1" strokeDasharray="2 3" />
      </svg>
    )
  }

  // Normalise to {o,h,l,c}; tolerate a close-only series (degrade to flat candles).
  let prev = bars[0].c
  const data = bars.map((b) => {
    const c = b.c
    const o = b.o ?? prev
    const hi = b.h ?? Math.max(o, c)
    const lo = b.l ?? Math.min(o, c)
    prev = c
    return { o, h: hi, l: lo, c }
  })

  const pad = 2
  const n0 = data.length

  // Line mode — directional area-line of closes (period direction = first→last).
  if (line) {
    const closes = data.map((d) => d.c)
    const clo = Math.min(...closes), chi = Math.max(...closes)
    const crange = (chi - clo) || 1
    const CX = (i) => pad + (i * (w - pad * 2)) / (n0 - 1)
    const CY = (p) => pad + (1 - (p - clo) / crange) * (h - pad * 2)
    const up = closes[n0 - 1] >= closes[0]
    const tok = up ? '--studio-success' : '--studio-danger'
    const linePath = 'M' + closes.map((v, i) => `${CX(i).toFixed(1)} ${CY(v).toFixed(1)}`).join(' L')
    const areaPath = `${linePath} L${CX(n0 - 1).toFixed(1)} ${h - pad} L${pad} ${h - pad} Z`
    return (
      <svg ref={ref} width={w} height={h} style={{ display: 'block', overflow: 'visible' }}>
        <path d={areaPath} fill={`hsl(var(${tok}) / 0.12)`} />
        <path d={linePath} fill="none" stroke={`hsl(var(${tok}))`} strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={CX(n0 - 1)} cy={CY(closes[n0 - 1])} r="1.7" fill={`hsl(var(${tok}))`} />
      </svg>
    )
  }

  // Candles need real width to read; ~3px each is the floor. (Line mode above
  // is a polyline — it handles density fine and keeps its detail.)
  const candles = bucket(data, Math.max(8, Math.floor((w - pad * 2) / 3)))

  const lo = Math.min(...candles.map((d) => d.l))
  const hi = Math.max(...candles.map((d) => d.h))
  // NB: `span`, not `range` — `range` is the timeframe prop.
  const span = (hi - lo) || 1
  const n = candles.length
  const slot = (w - pad * 2) / n
  const bw = Math.max(1.4, slot * 0.6)
  const X = (i) => pad + i * slot + slot / 2
  const Y = (p) => pad + (1 - (p - lo) / span) * (h - pad * 2)
  const col = (d) => (d.c >= d.o ? '--studio-success' : '--studio-danger')

  return (
    <svg ref={ref} width={w} height={h} style={{ display: 'block' }}>
      {candles.map((d, i) => {
        const c = `hsl(var(${col(d)}))`
        const bodyTop = Y(Math.max(d.o, d.c))
        const bodyH = Math.max(0.8, Math.abs(Y(d.o) - Y(d.c)))
        return (
          <g key={i}>
            <line x1={X(i)} x2={X(i)} y1={Y(d.h)} y2={Y(d.l)} stroke={c} strokeWidth="0.8" opacity="0.85" />
            <rect x={X(i) - bw / 2} y={bodyTop} width={bw} height={bodyH} fill={c} />
          </g>
        )
      })}
    </svg>
  )
}
